/**
 * useCart Hook
 *
 * React hook for cart operations
 * Provides clean interface for components
 */

import { useEffect } from 'react';
import { useCartStore } from '@/store/cartStore';
import type { CartItem } from '@/store/cartStore';

export interface UseCartReturn {
  // State
  items: CartItem[];
  total: number;
  itemCount: number;
  currency: string;
  isLoading: boolean;
  error: string | null;

  // Actions
  addItem: (itemCode: string, qty: number, notes?: string) => Promise<void>;
  updateQuantity: (itemCode: string, qty: number) => Promise<void>;
  removeItem: (itemCode: string) => Promise<void>;
  clearCart: () => Promise<void>;
  checkout: () => Promise<string>;
  refreshCart: () => Promise<void>;
}

/**
 * Hook for cart operations
 * Auto-loads cart on mount
 */
export function useCart(): UseCartReturn {
  const store = useCartStore();

  // Auto-fetch cart on mount
  useEffect(() => {
    store.fetchCart();
  }, []);

  return {
    // State
    items: store.items,
    total: store.total,
    itemCount: store.getItemCount(),
    currency: store.currency,
    isLoading: store.isLoading,
    error: store.error,

    // Actions
    addItem: store.addItem,
    updateQuantity: store.updateQuantity,
    removeItem: store.removeItem,
    clearCart: store.clearCart,
    checkout: store.checkout,
    refreshCart: store.fetchCart,
  };
}

/**
 * Hook for cart operations WITHOUT auto-fetch
 * Use this when you just need cart actions, not the full state
 */
export function useCartActions() {
  const store = useCartStore();

  return {
    addItem: store.addItem,
    updateQuantity: store.updateQuantity,
    removeItem: store.removeItem,
    clearCart: store.clearCart,
    checkout: store.checkout,
    refreshCart: store.fetchCart,
  };
}

/**
 * Hook for cart state only (no actions)
 * Lightweight for components that just display cart info
 */
export function useCartState() {
  const store = useCartStore();

  // Auto-fetch cart on mount
  useEffect(() => {
    store.fetchCart();
  }, []);

  return {
    items: store.items,
    total: store.total,
    itemCount: store.getItemCount(),
    currency: store.currency,
    isLoading: store.isLoading,
    error: store.error,
  };
}
